// API utility for making requests to the backend
// In development, requests go through the proxy (setupProxy.js)
// In production, requests go directly to the Render backend URL

const getApiBaseUrl = (): string => {
  // In production (on Vercel), use the environment variable
  // In development, use empty string (proxy handles it)
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || '';
  }
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

// Store for current user ID - set by auth context
let currentUserId: string | null = null;

/**
 * Set the current user ID for API requests
 * Call this when the user logs in
 */
export const setCurrentUserId = (userId: string | null): void => {
  currentUserId = userId;
  console.log(`API: User ID set to ${userId ? userId.substring(0, 8) + '...' : 'null'}`);
};

/**
 * Get the current user ID
 */
export const getCurrentUserId = (): string | null => {
  return currentUserId;
};

/**
 * Add userId to a URL as a query parameter
 */
const addUserIdToUrl = (endpoint: string): string => {
  if (!currentUserId) return endpoint;
  
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}userId=${currentUserId}`;
};

/**
 * Add userId to headers
 */
const addUserIdToHeaders = (headers?: HeadersInit): HeadersInit => {
  if (!currentUserId) return headers || {};
  
  return {
    ...headers,
    'x-user-id': currentUserId,
  };
};

/**
 * Make a fetch request to the backend API
 * Automatically adds userId to requests for security
 * @param endpoint - The API endpoint (e.g., '/api/accounts')
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns The fetch response
 */
export const apiFetch = async (
  endpoint: string,
  options?: RequestInit
): Promise<Response> => {
  const url = `${API_BASE_URL}${addUserIdToUrl(endpoint)}`;
  const enhancedOptions: RequestInit = {
    ...options,
    headers: addUserIdToHeaders(options?.headers),
  };
  return fetch(url, enhancedOptions);
};

/**
 * Make a GET request to the backend API
 * @param endpoint - The API endpoint
 * @param headers - Optional headers
 * @returns The fetch response
 */
export const apiGet = async (
  endpoint: string,
  headers?: HeadersInit
): Promise<Response> => {
  return apiFetch(endpoint, {
    method: 'GET',
    headers,
  });
};

/**
 * Make a POST request to the backend API
 * @param endpoint - The API endpoint
 * @param body - The request body (userId will be automatically added)
 * @param headers - Optional headers
 * @returns The fetch response
 */
export const apiPost = async (
  endpoint: string,
  body?: unknown,
  headers?: HeadersInit
): Promise<Response> => {
  // Add userId to POST body if it's an object
  let enhancedBody = body;
  if (currentUserId && body && typeof body === 'object' && !Array.isArray(body)) {
    enhancedBody = { ...body, userId: currentUserId };
  }
  
  return apiFetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: enhancedBody ? JSON.stringify(enhancedBody) : undefined,
  });
};
